import { useState, useEffect, useRef, useCallback } from "react";
import { useLingui } from "@lingui/react/macro";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";

interface MiniTimerProps {
  initialSeconds: number;
}

export function MiniTimer({ initialSeconds }: MiniTimerProps) {
  const { t } = useLingui();
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const tick = useCallback(() => {
    setTimeLeft((prev) => {
      if (prev <= 1) {
        setRunning(false);
        return 0;
      }
      return prev - 1;
    });
  }, []);

  useEffect(() => {
    if (running && timeLeft > 0) {
      intervalRef.current = setInterval(tick, 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, timeLeft, tick]);

  const reset = () => {
    setTimeLeft(initialSeconds);
    setRunning(false);
  };

  return (
    <div className="flex items-center gap-2">
      <Button size="sm" onClick={() => setRunning((r) => !r)} className="rounded-[10px]">
        {running ? t`Pause` : t`Start`}
        <Play className="size-4 fill-white" />
      </Button>
      <Button variant="link" onClick={reset} className="h-auto p-0 text-sm text-(--color-text-secondary) hover:text-(--color-text-primary)">
        {t`Reset`}
      </Button>
    </div>
  );
}
